import { createServerFn } from '@tanstack/react-start';

/*
 * Live competitor comparison proxy.
 *
 * The nsave backend has no multi-provider comparison endpoint of its own — the
 * only competitor rate/fee data the shared arrow-dev project consumes comes from
 * Wise's PUBLIC comparison API. arrow-dev's `WiseFxProvider`
 * (`apps/backend/src/fx-rate/providers/wise-fx.provider.ts` and
 * `apps/arq/src/fx/providers/wise-fx.provider.ts`) calls
 * `GET https://wise.com/gateway/v3/comparisons?sourceCurrency=…&targetCurrency=…&sendAmount=…`
 * (restricting `providers=wise` to read Wise's own mid rate). That same endpoint,
 * unrestricted, returns EVERY money-transfer provider it tracks for the corridor
 * (Wise, Remitly, Xoom, WorldRemit, Instarem, MoneyGram, OFX, banks …) with real
 * `rate`, `fee`, `markup` and `receivedAmount` per provider.
 *
 * The browser can't call `wise.com` directly (CORS + it 403s non-browser UAs),
 * so — exactly like `getQuote` in `quote.ts` — this `createServerFn` runs on our
 * own server (Nitro), fetches Wise server-to-server, normalizes, and returns the
 * competitor list to the client.
 *
 * Host is configurable via `WISE_COMPARISON_URL` (default the real Wise URL that
 * arrow-dev hardcodes) purely so it can be pointed at a mock in tests/dev.
 */

// Server-only runtime env (Nitro). Declared locally so we don't need @types/node.
declare const process: { env: Record<string, string | undefined> };

const DEFAULT_COMPARISON_URL = 'https://wise.com/gateway/v3/comparisons';
/** Clamp the send amount to a sane server-side range regardless of client input. */
const MAX_AMOUNT = 1_000_000;

/**
 * Preferred display order: mainstream consumer money-transfer brands first, so a
 * capped table shows recognizable competitors rather than niche banks. Anything
 * Wise returns that isn't listed here is appended after these, in API order.
 */
export const PREFERRED_PROVIDERS = [
  'wise',
  'remitly',
  'xoom',
  'world-remit',
  'instarem',
  'moneygram',
  'ofx',
] as const;

/** A single competitor's live quote for the requested corridor + amount. */
export type Competitor = {
  /** Wise provider alias, e.g. `wise`, `remitly`, `world-remit`. */
  alias: string;
  /** Human-readable provider name, e.g. `Wise`, `WorldRemit`. */
  name: string;
  /** Exchange rate (1 source unit -> N target units). */
  rate: number;
  /** Up-front transfer fee, in the SOURCE currency. */
  fee: number;
  /** Hidden exchange-rate markup cost, in the SOURCE currency. */
  markup: number;
  /** Amount the recipient receives, in the TARGET currency. */
  receivedAmount: number;
};

export type ComparisonInput = {
  sourceCurrency: string;
  targetCurrency: string;
  amount: number;
};

const CURRENCY_RE = /^[A-Z]{3}$/;

function validate(input: ComparisonInput): {
  sourceCurrency: string;
  targetCurrency: string;
  amount: number;
} {
  const sourceCurrency = String(input?.sourceCurrency ?? '').toUpperCase();
  const targetCurrency = String(input?.targetCurrency ?? '').toUpperCase();
  if (!CURRENCY_RE.test(sourceCurrency)) {
    throw new Error(`Invalid source currency: ${input?.sourceCurrency}`);
  }
  if (!CURRENCY_RE.test(targetCurrency)) {
    throw new Error(`Invalid target currency: ${input?.targetCurrency}`);
  }
  const rawAmount = Number(input?.amount);
  if (!Number.isFinite(rawAmount) || rawAmount <= 0) {
    throw new Error(`Invalid amount: ${input?.amount}`);
  }
  return { sourceCurrency, targetCurrency, amount: Math.min(rawAmount, MAX_AMOUNT) };
}

/** Stable sort by `PREFERRED_PROVIDERS` (listed first), then original API order. */
function byPreference(a: Competitor, b: Competitor): number {
  const order = PREFERRED_PROVIDERS as readonly string[];
  const ai = order.indexOf(a.alias);
  const bi = order.indexOf(b.alias);
  const ar = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
  const br = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;
  return ar - br;
}

type WiseQuote = {
  rate?: number;
  fee?: number;
  markup?: number;
  receivedAmount?: number;
};
type WiseProvider = { alias?: string; name?: string; quotes?: WiseQuote[] };
type WiseResponse = { providers?: WiseProvider[] };

/**
 * Fetch live competitor quotes for a corridor from Wise's public comparison API.
 * Runs server-side only. Throws on invalid input or upstream failure; the caller
 * (`RateComparison`) falls back to its own demo competitors so the table never
 * shows a broken state.
 */
export const getComparison = createServerFn({ method: 'GET' })
  .validator((input: ComparisonInput) => input)
  .handler(async ({ data }): Promise<Competitor[]> => {
    const { sourceCurrency, targetCurrency, amount } = validate(data);

    const url = new URL(process.env.WISE_COMPARISON_URL || DEFAULT_COMPARISON_URL);
    url.searchParams.set('sourceCurrency', sourceCurrency);
    url.searchParams.set('targetCurrency', targetCurrency);
    url.searchParams.set('sendAmount', String(amount));

    let response: Response;
    try {
      response = await fetch(url, {
        headers: {
          accept: 'application/json',
          // Wise's public endpoint 403s requests without a browser-like UA.
          'user-agent':
            'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0 Safari/537.36',
        },
        signal: AbortSignal.timeout(8000),
      });
    } catch (cause) {
      throw new Error(`Comparison upstream unreachable: ${String(cause)}`, { cause });
    }

    if (!response.ok) {
      throw new Error(`Comparison upstream error: ${response.status} ${response.statusText}`);
    }

    const body = (await response.json()) as WiseResponse;

    const competitors = (body.providers ?? []).flatMap((provider): Competitor[] => {
      const quote = provider.quotes?.[0];
      if (!provider.alias || !provider.name || !quote) return [];
      const rate = Number(quote.rate);
      const fee = Number(quote.fee ?? 0);
      const markup = Number(quote.markup ?? 0);
      const receivedAmount = Number(quote.receivedAmount);
      if (!Number.isFinite(rate) || !Number.isFinite(receivedAmount) || receivedAmount <= 0) {
        return [];
      }
      return [
        {
          alias: provider.alias,
          name: provider.name,
          rate,
          fee: Number.isFinite(fee) ? fee : 0,
          // Wise occasionally reports a tiny negative markup; clamp to 0.
          markup: Number.isFinite(markup) ? Math.max(markup, 0) : 0,
          receivedAmount,
        },
      ];
    });

    return competitors.sort(byPreference);
  });
